import { useReducer, useCallback, createContext, useEffect } from 'react';
import { GameSounds } from 'components/modules/Activities/SoundOutWordsActivity/transformers';
import { getOrderedWords } from 'components/modules/Activities/SoundOutWordsActivity/utils/sound-out-words-util';
import useGame from '@monorepo/shared/src/hooks/use-game';
import useProgress from '@monorepo/shared/src/hooks/use-progress';
import useActivityAudio from '@monorepo/shared/src/hooks/use-activity-audio';
import useTelemetry from '@monorepo/shared/src/hooks/use-telemetry';
import { ActivityTypes, TelemetryEvents } from '@monorepo/shared/src/types';
import {
  ApplicationSection,
  PageLayout,
  SoundOutWordsPage,
} from '@monorepo/shared/src/graph/schema-generated-types-educator';
import { initialState, reducers, spelledCharacterInitialState } from './reducers';
import { UseSoundOutWordsState, SpellableWordProps, Actions } from './types';
import { formatCharacter, formatPageNumber } from '@monorepo/shared/src/util/telemetry-util';

export const SoundOutWordsContext = createContext<any>([]);

export const SoundOutWordsProvider: React.FC<any> = ({ children }) => {
  const [, { fireEvent }] = useTelemetry(TelemetryEvents.ACCESS_ACTIVITY_PAGE);
  const [{ activityData, currentData }] = useGame<SoundOutWordsPage>();
  const [, { setTotal, setFound }] = useProgress();
  const [{ isPlaying }, { playSound, stopSound }] = useActivityAudio();
  const [state, dispatch] = useReducer<React.Reducer<UseSoundOutWordsState, Actions>>(
    reducers,
    initialState
  );

  const setOrderedWords = useCallback(
    (orderedWords: SpellableWordProps[]) => {
      dispatch({ type: 'SET_ORDERED_WORDS', payload: { orderedWords } });
    },
    [state, dispatch]
  );

  const setSelectedWord = async (index: number): Promise<void> => {
    const selectedWord = state.orderedWords.find(({ order }) => index === order);

    if (!selectedWord) return;

    const { sounds, page, wordIndexOnPage, capitalize } = selectedWord;

    dispatch({
      type: 'SET_SELECTED_WORD',
      payload: {
        page,
        selectedWord: { text: sounds.map((sound) => sound.description), index, capitalize },
      },
    });

    dispatch({
      type: 'SET_VISITED_WORDS',
      payload: { page, index: wordIndexOnPage },
    });

    await playSounds(sounds);

    setSpelledCharacter(spelledCharacterInitialState.character, spelledCharacterInitialState.index);

    if (!state.pages[page]?.visitedWords?.includes(wordIndexOnPage)) {
      setFound(page);
    }
  };

  const setSpelledCharacter = (char: string, i: number) => {
    dispatch({
      type: 'SET_SPELLED_CHARACTER',
      payload: { spelledCharacter: { character: char, index: i } },
    });
  };

  const playSounds = async (sounds: any[]) => {
    for (let index = 0; index < sounds.length; index++) {
      const { description } = sounds[index];
      setSpelledCharacter(description, index);
      await playSound(GameSounds.CHARACTER_AUDIO(description));
    }
  };

  useEffect(() => {
    if (!currentData) return;

    const book = activityData?.book;
    const page = currentData?.[0]?.page;
    if (!book || !page) return;

    const character = { ...book.characters?.[0] };

    fireEvent({
      activityType: ActivityTypes.SOUND_OUT_WORDS,
      bookId: book.id,
      bookCode: book.bookCode,
      pageId: page.id,
      pageNumber: formatPageNumber(page.pageNumber as any),
      pageLayout: currentData?.length > 1 ? PageLayout.DOUBLE_PAGE : PageLayout.SINGLE_PAGE,
      section: ApplicationSection.LETTER_LEARNING,
      character: formatCharacter(character),
    });
  }, [currentData]);

  useEffect(() => {
    const orderedWords = getOrderedWords(currentData);

    setOrderedWords(orderedWords);

    currentData.forEach((curr) =>
      setTotal(
        curr.page?.pageNumber ?? 0,
        orderedWords.filter(({ page }) => page === curr.page?.pageNumber).length
      )
    );

    if (isPlaying) stopSound();
  }, [currentData]);

  return (
    <SoundOutWordsContext.Provider
      value={[
        state,
        {
          setSelectedWord,
          setSpelledCharacter,
        },
      ]}
    >
      {children}
    </SoundOutWordsContext.Provider>
  );
};

export default SoundOutWordsProvider;
